import { Card } from "@rumbledirect/ui";
import React from "react";

type EmailTemplatePreviewProps = {
  title: string;
  html: string | undefined;
};

const decodeHtmlEntities = (value: string) => {
  let prev: string | null = null;
  let current = value;
  let iterations = 3;
  while (
    iterations-- > 0 &&
    current !== prev &&
    /&[a-zA-Z#0-9]+;/.test(current)
  ) {
    prev = current;
    const ta = document.createElement("textarea");
    ta.innerHTML = current;
    current = ta.value;
  }
  return current;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const stripTwigPreserveOutputs = (value: string) =>
  value
    // Remove Twig comments {# ... #}
    .replace(/{#[\s\S]*?#}/g, "")
    // Remove Twig statements/blocks {% ... %}
    .replace(/{%[\s\S]*?%}/g, "")
    // If href/src contains Twig, neutralize entire attribute value to '#'
    .replace(/\s(href|src)\s*=\s*"[^"]*{{[\s\S]*?}}[^"]*"/gi, ' $1="#"')
    .replace(/\s(href|src)\s*=\s*'[^']*{{[\s\S]*?}}[^']*'/gi, " $1='#'")
    // Remove Twig outputs inside any other attribute values (keep surrounding text)
    .replace(
      /="([^"]*){{[\s\S]*?}}([^"]*)"/g,
      (_m, pre: string, post: string) => `="${pre}${post}"`,
    )
    .replace(
      /='([^']*){{[\s\S]*?}}([^']*)'/g,
      (_m, pre: string, post: string) => `='${pre}${post}'`,
    )
    // Render remaining Twig output expressions {{ ... }} in text nodes as grey placeholders
    .replace(/{{\s*([\s\S]*?)\s*}}/g, (_m, expr: string) => {
      const placeholder = escapeHtml(`{{${expr}}}`);
      return `<span style=\"color: #ccc;\">${placeholder}</span>`;
    });

const sanitizeBasic = (value: string) =>
  value
    // Remove script tags completely
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    // Neutralize inline event handlers like onclick="..."
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    // Block javascript: in href/src
    .replace(/\s(href|src)\s*=\s*"javascript:[^"]*"/gi, ' $1="#"')
    .replace(/\s(href|src)\s*=\s*'javascript:[^']*'/gi, " $1='#'");

export const EmailTemplatePreview: React.FC<EmailTemplatePreviewProps> = ({
  html,
  title,
}) => {
  let bodyContent = "";
  if (html !== undefined) {
    bodyContent = stripTwigPreserveOutputs(decodeHtmlEntities(html)).trim();
  }
  let previewInnerHtml = sanitizeBasic(bodyContent);

  return (
    <Card title={title}>
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: previewInnerHtml }}
      />
    </Card>
  );
};
